// ============================================================
// KF CONSUMER — ProductCard (feed de productos)
// ============================================================
// Consumidor del VCE bajo el CONTRATO: describe ÚNICAMENTE
//   • Qué contenido posee (media/images, nombre, precio, estado)
//   • En qué contexto se encuentra ('card' — feed)
//   • Qué configuración de tenant utiliza (kfFeedConfig)
// y entrega esa información al Visual Composition Engine.
// Las regiones de INFORMACIÓN (header/body/footer/overlay) son
// contenido compuesto por el consumidor (ReactNode); la COMPOSICIÓN
// (variant, ratio, capacidades, presentación, fit) la decide el motor.
// Resultado visual IDÉNTICO al feed certificado F1 (paridad absoluta).
// ============================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SmartComposer } from '../Common/visual-engine';
import StatusBadge from '../Common/StatusBadge';
import { kfFeedConfig } from './visualEngine.config';

export interface ProductCardProduct {
  code: string;
  name: string;
  price?: number;
  category?: string;
  status?: string;
  media?: Array<{ type?: 'image' | 'video'; url: string }>;
  images?: string[];
  manualBlockedBy?: string | null;
  currentCart?: unknown;
}

interface ProductCardProps {
  product: ProductCardProduct;
  isInCart: boolean;
  isBlocked: boolean;
  /** Click de la tarjeta (lo decide el contexto del feed — p. ej. operador vs cliente) */
  onOpen?: (code: string) => void;
  variant?: 'feed';
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, isInCart, isBlocked, onOpen, variant = 'feed' }) => {
  const navigate = useNavigate();

  // ── Contenido del overlay (estado de negocio → badge; el consumidor compone) ──
  const overlay = (() => {
    if (isBlocked && !isInCart && product.manualBlockedBy) {
      return (
        <div className="absolute inset-0 bg-yellow-900 bg-opacity-20 backdrop-blur-[1px] flex items-center justify-center">
          <StatusBadge label="En gestión" tone="yellow" />
        </div>
      );
    }
    if (isBlocked && !isInCart && product.status === 'sold') {
      return (
        <div className="absolute inset-0 bg-purple-900 bg-opacity-20 backdrop-blur-[1px] flex items-center justify-center">
          <StatusBadge label="Vendido" tone="purple" />
        </div>
      );
    }
    if (isBlocked && !isInCart && product.status === 'verifying') {
      return (
        <div className="absolute inset-0 bg-purple-900 bg-opacity-20 backdrop-blur-[1px] flex items-center justify-center">
          <StatusBadge label="En verificación de pago" tone="purple" />
        </div>
      );
    }
    if (isBlocked && !isInCart && product.status !== 'sold' && product.status !== 'verifying' && product.status !== 'in_cart') {
      return (
        <div className="absolute inset-0 bg-purple-900 bg-opacity-20 backdrop-blur-[1px] flex items-center justify-center">
          <StatusBadge label="En proceso de compra" tone="purple" />
        </div>
      );
    }
    if (isInCart) {
      return (
        <div className="absolute inset-0 bg-green-900 bg-opacity-20 flex items-center justify-center">
          <StatusBadge label="En tu carrito" tone="green" />
        </div>
      );
    }
    return null;
  })();

  // ── Contenido de acciones (región footer — el motor ancla al fondo) ──
  const footer = (
    <div>
      {!isBlocked && (
        <button onClick={(e) => { e.stopPropagation(); navigate('/producto/' + product.code); }}
          className="w-full py-2 px-4 rounded-lg font-medium bg-purple-600 text-white hover:bg-purple-700">
          Ver detalles
        </button>
      )}
      {isBlocked && isInCart && (
        <div className="mt-2 space-y-2">
          <button onClick={(e) => { e.stopPropagation(); navigate('/carrito'); }}
            className="w-full py-2 px-4 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900">🛒 Ir al carrito</button>
          <button onClick={(e) => { e.stopPropagation(); navigate('/checkout'); }}
            className="w-full py-2 px-4 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">💬 Checkout</button>
        </div>
      )}
      {isBlocked && !isInCart && product.status === 'sold' && (
        <div className="w-full py-2 px-4 rounded-lg font-medium bg-gray-300 text-gray-500 text-center text-sm cursor-not-allowed">Vendido</div>
      )}
      {isBlocked && !isInCart && product.status === 'verifying' && (
        <div className="w-full py-2 px-4 rounded-lg font-medium bg-gray-300 text-gray-500 text-center text-sm cursor-not-allowed">En verificación de pago</div>
      )}
      {isBlocked && !isInCart && product.manualBlockedBy && (
        <div className="w-full py-2 px-4 rounded-lg font-medium bg-yellow-50 text-yellow-700 text-center text-sm border border-yellow-200">En gestión por operador</div>
      )}
      {isBlocked && !isInCart && !product.manualBlockedBy && product.status !== 'sold' && product.status !== 'verifying' && (
        <div className="w-full py-2 px-4 rounded-lg font-medium bg-gray-300 text-gray-500 text-center text-sm cursor-not-allowed">En proceso de compra</div>
      )}
    </div>
  );

  // ── DESCRIPTORES NEUTROS del perfil (los mide el consumidor; el motor decide) ──
  // state: token neutro derivado del estado KF (significado = tenant, token = motor)
  const neutralState = isBlocked
    ? (product.status === 'sold' ? 'sold'
        : product.status === 'verifying' ? 'verifying'
        : 'blocked')
    : 'active';
  const description = (product as { description?: string }).description?.trim() ?? '';
  const infoLength = (product.name?.length ?? 0) + String(product.price ?? 0).length + description.length;
  // actionCount: acciones reales del footer (lo que el motor puede priorizar)
  const actionCount = !isBlocked ? 1 : (isBlocked && isInCart ? 2 : 0);

  return (
    <SmartComposer
      media={product.media}
      images={product.images}
      alt={product.name}
      blocked={isBlocked}
      infoLength={infoLength}
      hasDescription={description.length > 0}
      state={neutralState}
      actionCount={actionCount}
      context="card"
      config={kfFeedConfig}
      onClick={() => onOpen?.(product.code)}
      className={
        (isBlocked ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl cursor-pointer') +
        ' transition-all duration-300'
      }
      overlay={overlay}
      header={
        <div className="p-4 pb-0">
          <div className="flex justify-between items-center mb-3">
            <span className="bg-purple-200 text-purple-700 text-xs px-2 py-1 rounded">{product.category}</span>
            <span className="text-sm font-mono text-gray-800 bg-gray-200 px-2 py-1 rounded">{product.code}</span>
          </div>
        </div>
      }
      body={
        <div className="px-4 pt-1">
          <h3 className={'font-bold mb-1 ' + (isBlocked ? 'text-gray-500' : 'text-gray-900')}>{product.name}</h3>
          <div className={'text-2xl font-bold ' + (isBlocked ? 'text-gray-400' : 'text-gray-900')}>
            RD$ {(product.price || 0).toLocaleString()}
          </div>
          {description.length > 0 && (
            <p className="mt-2 text-sm text-gray-600 leading-snug">{description}</p>
          )}
        </div>
      }
      footer={<div className="p-4">{footer}</div>}
    />
  );
};

export default ProductCard;
